import { Text } from '@televet/kibble-ui/build/components/Text';
import { Flex } from '@televet/kibble-ui/build/chakra';
import { PetAgeForm } from 'pages/Automations/components/Layout/PetAgeForm';
import { PatientAgeUnit } from 'pages/Automations/types/SendConditionPatientAge';
import React from 'react';
import { SegmentWrapper } from './SegmentWrapper';
import { ConditionProps, SegmentPetAgeUnit, SegmentPetAgeYoungerOrOlder } from '../types/conditionalSegments.types';
import { capitalize, formatPluralization } from 'shared/utils';

const petAgeOperatorOptions = [
  { label: 'Younger', value: SegmentPetAgeYoungerOrOlder.younger },
  { label: 'Older', value: SegmentPetAgeYoungerOrOlder.older },
];

type PetAgeSegmentConditionValue = {
  unit: SegmentPetAgeUnit;
  youngerOrOlder: SegmentPetAgeYoungerOrOlder;
  amount: number;
};

type PetAgeSegmentProps = ConditionProps<PetAgeSegmentConditionValue>;

export const PetAgeSegment = ({
  onDelete,
  conditionValue,
  updateConditionValue,
  asSummary,
  displayValidation,
}: PetAgeSegmentProps): JSX.Element => {
  const { youngerOrOlder, amount } = conditionValue;
  const unit = conditionValue.unit === SegmentPetAgeUnit.months ? PatientAgeUnit.Months : PatientAgeUnit.Years;

  if (asSummary) {
    return (
      <Text fontWeight="light" color="text.subtle">
        Patient Age: {capitalize(youngerOrOlder)} than {amount.toString()}{' '}
        {formatPluralization({
          word: unit === PatientAgeUnit.Months ? 'Month' : 'Year',
          singularCriteria: amount,
        })}
      </Text>
    );
  }

  return (
    <SegmentWrapper onDelete={onDelete}>
      <>
        <Flex align="center" gap="3">
          <Text fontWeight="bold">Patient Age</Text>
          <PetAgeForm
            patientAgeOperatorOptions={petAgeOperatorOptions}
            operator={youngerOrOlder}
            handleAgeOperatorChange={({ value }): void => {
              updateConditionValue({ ...conditionValue, youngerOrOlder: value as SegmentPetAgeYoungerOrOlder });
            }}
            operandValue={amount}
            displayUnit={unit}
            handleAgeValueChange={(newAmount): void => {
              updateConditionValue({ ...conditionValue, amount: Number(newAmount) });
            }}
            handleAgeDisplayUnitChange={({ value }): void => {
              const _unit = value === PatientAgeUnit.Months ? SegmentPetAgeUnit.months : SegmentPetAgeUnit.years;
              updateConditionValue({ ...conditionValue, unit: _unit });
            }}
          />
        </Flex>

        {displayValidation && (
          <Text as="p" mt={2} color="text.danger">
            Please provide a valid number.
          </Text>
        )}
      </>
    </SegmentWrapper>
  );
};
